/* eslint-disable no-underscore-dangle */
import {
  QueryKey,
  useInfiniteQuery as useReactInfiniteQuery,
  UseInfiniteQueryResult,
  InfiniteData
} from '@tanstack/react-query'
import axios from 'axios'

import { useClient } from '../useClient/useClient'

import { InfiniteQueryFn, UseInfiniteQueryOptions, ListResponse } from './useInfiniteQuery.types'

export const shouldRetryQuery = (
  failureCount: number,
  error: unknown,
  additionalRetryStatusCodes: number[] = []
): boolean => {
  if (axios.isAxiosError(error)) {
    if (failureCount >= 3) return false

    const status = error.response?.status

    if (!status) return false

    if (status >= 500) return true

    if (additionalRetryStatusCodes?.includes(status)) return true
  }

  return false
}

export const calculateRetryDelay = (attemptIndex: number) => Math.min(1000 * attemptIndex, 30000)

export const useInfiniteQuery = <
  TResponse extends ListResponse<unknown>,
  TQueryKey extends QueryKey = unknown[],
  TParams = unknown,
  TError = unknown
>(
  queryKey: TQueryKey,
  query: InfiniteQueryFn<TQueryKey, string, TResponse>,
  options?: UseInfiniteQueryOptions<TResponse, TParams, TError, InfiniteData<TResponse, string>, TQueryKey, string>
): UseInfiniteQueryResult<InfiniteData<TResponse, string>, TError> => {
  const { gsClient } = useClient()

  return useReactInfiniteQuery<TResponse, TError, InfiniteData<TResponse, string>, TQueryKey, string>({
    queryKey,
    initialPageParam: '',
    queryFn: ctx => query(gsClient, ctx.queryKey as TQueryKey, ctx.pageParam),
    ...options,
    refetchOnWindowFocus: !!options?.refetchOnWindowFocus,
    retry: (failureCount, error) => shouldRetryQuery(failureCount, error, options?.additionalRetryStatusCodes),
    retryDelay: attemptIndex => calculateRetryDelay(attemptIndex),
    getNextPageParam: lastPage => lastPage._links?.next
  })
}
